import { useSiteBrand } from "@/data/siteBrand";
import { phoneHref, useSiteContact } from "@/data/siteContact";
import styles from "./FooterSection.module.scss";
import { getSiteLocale, localizeHref, stripLocalePrefix } from "@/i18n/locale";
import { translateText, uiText } from "@/i18n/translations";

export function FooterSection() {
  const year = new Date().getFullYear();
  const brand = useSiteBrand();
  const contact = useSiteContact();
  const locale = getSiteLocale();
  const isContactPage = typeof window !== "undefined" && stripLocalePrefix(window.location.pathname).replace(/\/+$/, "") === "/contact";
  const conversationDetails: ReadonlyArray<{ label: string; value: string; href?: string }> = [
    { label: translateText("Email", locale), value: contact.email, href: `mailto:${contact.email}` },
    { label: translateText("Phone", locale), value: contact.landlinePhone, href: phoneHref(contact.landlinePhone) },
    { label: translateText("Mobile", locale), value: contact.mobilePhone, href: phoneHref(contact.mobilePhone) },
    { label: translateText("Base", locale), value: contact.address },
  ];

  return (
    <footer className={styles.footer} data-site-footer data-chrome-surface="dark">
      <div className={styles.topline}>
        <div className={styles.toplineCopy}>
          <span className={styles.eyebrow}>{brand.footer.eyebrow}</span>
          <p className={styles.kicker}>{brand.footer.kicker}</p>
        </div>
        {!isContactPage && (
          <a className={styles.cta} href={localizeHref(brand.footer.cta_href, locale)} data-magnetic>
            {brand.footer.cta_label}
          </a>
        )}
      </div>

      <div className={styles.grid}>
        <div className={styles.inner}>
          <h2 className={styles.headline}>
            {brand.footer.headline_lines.map((line, index) => (
              <span key={`${line}-${index}`}>
                {index > 0 && <br />}
                {line}
              </span>
            ))}
          </h2>
        </div>

        <div className={styles.sidePanel}>
          <p className={styles.body}>{brand.footer.body}</p>

          <div className={styles.detailList}>
            {conversationDetails.map((item) => (
              <div key={item.label} className={styles.detailItem}>
                <span>{item.label}</span>
                {item.href ? (
                  <a href={item.href}>{item.value}</a>
                ) : (
                  <strong>{item.value}</strong>
                )}
              </div>
            ))}
            {brand.social_links.length > 0 && (
              <div className={styles.detailItem}>
                <span>{translateText("Follow", locale)}</span>
                <div className={styles.socialLinks}>
                  {brand.social_links.map((link) => (
                    <a key={link.label} href={link.url} target="_blank" rel="noreferrer noopener">
                      {link.label}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.mapEmbed} aria-label={`${uiText("Map showing", locale)} ${contact.address}`}>
        <iframe
          title={uiText("VenueX business address map", locale)}
          src={contact.mapsEmbedUrl}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      </div>

      <div className={styles.footerBar}>
        <nav className={styles.nav} aria-label={uiText("Footer", locale)}>
          {brand.footer.navigation.map((link) => (
            <a key={link.href} href={localizeHref(link.href, locale)} className={styles.navLink} data-magnetic>
              {link.label}
            </a>
          ))}
        </nav>

        <div className={styles.bottom}>
          <span className={styles.copyright}>
            &copy; {brand.footer.copyright.replace("{year}", String(year))}
          </span>
          <a
            className={styles.credit}
            href={brand.footer.credit_url}
            target="_blank"
            rel="noreferrer"
            aria-label={`${brand.brand_name}. ${brand.footer.credit_label} ${brand.footer.credit_name}`}
          >
            <span className={styles.creditLabel}>{translateText(brand.footer.credit_label, locale)}</span>
            <img className={styles.creditLogo} src={brand.footer.credit_logo_url} alt={brand.footer.credit_name} />
          </a>
        </div>
      </div>
    </footer>
  );
}
